import type { MutableRefObject } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useSelfUpdatingRef } from '@hotelplan/libs.hooks-react';

interface IAnyCallback<TArgs extends unknown[] = [], TReturn = void> {
  (...args: TArgs): TReturn;
}

type IOpenConfirmation = IAnyCallback;

export interface IConfirmationProps<
  TArgs extends unknown[] = [],
  TReturn = void
> {
  isShow: boolean;
  onApply: (...args: TArgs) => TReturn;
  onCancel: () => void;
}

export interface IConfirmationOptions {
  closeOnApply?: boolean;
}

export function useConfirmation<TArgs extends unknown[], TReturn = void>(
  callbackRef:
    | MutableRefObject<IAnyCallback<TArgs, TReturn>>
    | IAnyCallback<TArgs, TReturn>,
  options?: IConfirmationOptions
): [IOpenConfirmation, IConfirmationProps<TArgs, TReturn>] {
  const optionsRef = useSelfUpdatingRef(options);
  const [isShow, setIsShow] = useState<boolean>(false);

  const open = useCallback(() => {
    setIsShow(true);
  }, []);

  const onApply = useCallback(
    (...args: TArgs): TReturn => {
      let ret: TReturn;

      if ('current' in callbackRef) {
        ret = callbackRef.current(...args);
      } else {
        ret = callbackRef(...args);
      }

      if (optionsRef.current?.closeOnApply) {
        setIsShow(false);
      }

      return ret;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [callbackRef]
  );

  const onCancel = useCallback(() => {
    setIsShow(false);
  }, []);

  return useMemo(() => {
    return [
      open,
      {
        isShow,
        onApply,
        onCancel,
      },
    ];
  }, [isShow, onApply, onCancel, open]);
}
